import React from 'react';
import ReactMarkdown from 'react-markdown';
import { LogoCard, Heading } from '@/components/v2/global';
import Button from '@/components/v2/global/Button';
import { Model } from '@/types/model';
import { formatModelInfo, handleDeploy } from '@/utilities/model-utils';
import { getModelLogo } from '@/utilities/model-logo-utils';

interface HeroSectionProps {
    // Model-specific props (for model detail pages)
    model?: Model;

    // Generic props (for other pages like model-library)
    title?: string;
    description?: string;
    subtitle?: string;
    showLogo?: boolean;
    showButton?: boolean;
    buttonText?: string;
    buttonAction?: () => void;

    // Custom content (for complex layouts like search)
    children?: React.ReactNode;

    // Styling props
    height?: string;
    className?: string;
    justify?: 'left' | 'center' | 'right';
}

export const HeroSection = ({
    model,
    title,
    description,
    subtitle,
    showLogo = false,
    showButton = false,
    buttonText = "Deploy This Model",
    buttonAction,
    children,
    height = '39rem',
    className = '',
    justify = 'center'
}: HeroSectionProps) => {
    // If model is provided, use model data; otherwise use generic props
    const heroTitle = model ? model.displayName : title;
    const heroDescription = model ? model.description : description;
    const heroSubtitle = model ? formatModelInfo(model.modelType, model.size, model.parameters) : subtitle;
    const isModelPage = !!model;

    // Helper function to get alignment classes
    const getAlignmentClasses = (justification: 'left' | 'center' | 'right') => {
        const alignmentMap = {
            left: 'items-start text-left',
            center: 'items-center text-center',
            right: 'items-end text-right'
        };
        return alignmentMap[justification];
    };

    // Get layout classes
    const alignmentClasses = getAlignmentClasses(justify);
    const layoutClasses = isModelPage ? 'justify-start pt-12' : 'justify-center mt-20';
    const containerClasses = `flex flex-col ${alignmentClasses} ${layoutClasses}`;

    return (
        <section
            className={`bg-white text-black pt-14 lg:pt-20 pb-16 relative overflow-hidden ${isModelPage ? 'min-h-[39rem]' : 'flex justify-center items-center'} ${className}`}
            style={{
                height: isModelPage ? 'auto' : height
            }}
        >
            <img
                src="/texture-ellipsed.png"
                alt="Texture"
                className="absolute inset-0 w-full h-full object-cover z-0"
                style={{ opacity: 0.6, filter: 'contrast(2) brightness(0.7)' }}
            />
            <div className="container relative z-10 px-4 sm:px-6 lg:px-8">
                <div className={containerClasses}>
                    {heroSubtitle && (
                        <p className="text-sm text-orange mb-2">
                            {heroSubtitle}
                        </p>
                    )}
                    {heroTitle && (
                        <Heading className="mb-6 px-4">
                            {heroTitle}
                        </Heading>
                    )}
                    {heroDescription && (
                        <div className={`text-base sm:text-lg opacity-70 max-w-2xl text-black mb-8 px-16 ${justify === 'left' ? 'text-left' : justify === 'right' ? 'text-right' : 'text-center'}`}>
                            {model ? (
                                <ReactMarkdown
                                    components={{
                                        p: ({ children }) => <p className="mb-4 last:mb-0">{children}</p>,
                                        a: ({ href, children }) => (
                                            <a
                                                href={href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:text-blue-800 underline"
                                            >
                                                {children}
                                            </a>
                                        )
                                    }}
                                >
                                    {heroDescription}
                                </ReactMarkdown>
                            ) : (
                                <p>{heroDescription}</p>
                            )}
                        </div>
                    )}

                    {/* Custom children content (like search box) */}
                    {children}

                    {(showLogo || model) && (
                        <LogoCard
                            logo={
                                <div
                                    className="svg-logo-container"
                                    style={{
                                        transform: 'scale(1.5)',
                                        transformOrigin: 'center',
                                        width: 'fit-content',
                                        height: 'fit-content'
                                    }}
                                >
                                    {getModelLogo({ model, type: 'flat' })}
                                </div>
                            }
                            alt={`${model?.family || 'Model'} logo`}
                            className="mb-6"
                        />
                    )}
                    {(showButton || model) && (
                        <Button
                            variant="primary"
                            className="sm:w-auto mb-8"
                            onClick={model ? () => handleDeploy(model.modelShortString) : buttonAction}
                        >
                            {buttonText}
                        </Button>
                    )}
                </div>
            </div>
        </section>
    );
};
